import { environment } from 'src/environments/environment';
import { z } from 'zod';
import { Currency } from 'src/app/model/wallet/currency';
import { HttpService } from '../../services/http/http.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { ApiResponseSchema } from '../api-response';
import { CreateInvoiceData, CreateInvoiceSchema } from './schemas/create-invoice';
import { DepositStatusData, DepositStatusSchema } from './schemas/deposit-status';
import { LightningAddressData, LightningAddressSchema } from './schemas/lightning-address';
import { ApiSchema } from '../api-schema';

export const JIT_ENDPOINTS = {
  CREATE_INVOICE: 'jit/create-invoice',
  DEPOSIT_STATUS: 'jit/deposit-status',
  SET_LIGHTNING_ADDRESS: 'accounts/set-lightning-address',
} as const;

export class JitApi {
  private api: ApiSchema;

  private constructor(httpService: HttpService, analyticsService: AnalyticsService) {
    // JIT also uses the wallets cloud project
    this.api = new ApiSchema(environment.walletApi, httpService, analyticsService);
  }

  public static request(httpService: HttpService, analyticsService: AnalyticsService) {
    return new JitApi(httpService, analyticsService);
  }

  public async createInvoice(usd: number): Promise<CreateInvoiceData> {
    const CreateInvoiceResponseSchema = ApiResponseSchema(CreateInvoiceSchema);
    type CreateInvoiceResponse = z.infer<typeof CreateInvoiceResponseSchema>;

    const response = await this.api.post<
      CreateInvoiceResponse,
      { amount: number; currency: string }
    >(JIT_ENDPOINTS.CREATE_INVOICE, CreateInvoiceResponseSchema, {
      amount: usd,
      currency: Currency.USD,
    });
    return response.data;
  }

  public async depositStatus(depositId: string): Promise<DepositStatusData> {
    const DepositStatusResponseSchema = ApiResponseSchema(DepositStatusSchema);
    type DepositStatusResponse = z.infer<typeof DepositStatusResponseSchema>;

    const response = await this.api.get<DepositStatusResponse>(
      `${JIT_ENDPOINTS.DEPOSIT_STATUS}/${depositId}`,
      DepositStatusResponseSchema,
    );
    return response.data;
  }

  public async setLightningAddress(lightningAddress: string): Promise<LightningAddressData> {
    const LightningAddressResponseSchema = ApiResponseSchema(LightningAddressSchema);
    type LightningAddressResponse = z.infer<typeof LightningAddressResponseSchema>;

    const response = await this.api.post<LightningAddressResponse, { lightningAddress: string }>(
      JIT_ENDPOINTS.SET_LIGHTNING_ADDRESS,
      LightningAddressResponseSchema,
      { lightningAddress },
    );
    return response.data;
  }
}
